import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSplitAnalytics } from '../../../hooks/useAnalytics';
import { useNormalizedSplitData } from '../../../hooks/useNormalizedData';
import MetricCard from '../core/MetricCard';
import KPICard from '../core/KPICard';
import AreaChart from '../../ui/AreaChart';
import BarChart from '../../ui/BarChart';
import PieChart from '../../ui/PieChart';
import DataTable from '../../ui/DataTable';
import CollapsibleSection from '../../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../../services/chartService';
import { getPreviousPeriodValue } from '../../../utils/chartHelpers';
import { formatPercent, safeFormatFixed } from '../../../utils/format';
import { useFormatting } from '../../../hooks/useFormatting';
import { FILTER_PERIODS } from '../../../constants';
import './SplitMetrics.css';

/**
 * Split Metrics Component
 * Displays split-related analytics with thin, clean design
 * @param {Object} props - Component props
 * @param {string} props.period - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const SplitMetrics = ({ period, compact = false }) => {
  const { data: splitData, loading, error } = useSplitAnalytics(period);

  // Normalize data for safe access
  const normalized = useNormalizedSplitData(splitData);

  // Generate chart data for key metrics (must be before any early returns)
  const [totalSplitsChart, setTotalSplitsChart] = useState(null);
  const [totalAmountChart, setTotalAmountChart] = useState(null);
  const [previousSplitData, setPreviousSplitData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getSplitAnalytics } = await import('../../../services/firebaseService');
        const prevSplit = await getSplitAnalytics(prevPeriod).catch(() => null);
        setPreviousSplitData(prevSplit);
      } catch (error) {
        setPreviousSplitData(null);
      }
    };

    if (splitData) {
      fetchPreviousPeriodData();
    }
  }, [period, splitData]);

  useEffect(() => {
    if (!splitData) {
      setTotalSplitsChart(null);
      setTotalAmountChart(null);
      return;
    }

    // Use actual previous period data if available, otherwise calculate from growth or use estimate
    const currentValue = splitData.totalSplits || 0;
    const previousValue = getPreviousPeriodValue(splitData, previousSplitData, 'totalSplits', 0.85);
    
    const currentAmount = splitData.totalAmount || 0;
    const previousAmount = getPreviousPeriodValue(splitData, previousSplitData, 'totalAmount', 0.85);
    
    // Try to fetch real data first, then fallback to generated
    Promise.all([
      transformToChartDataAsync(
        { totalSplits: currentValue },
        { totalSplits: previousValue },
        'totalSplits',
        period,
        {
          collectionName: 'splits',
          valueExtractor: (docData) => 1 // Count splits
        }
      ),
      transformToChartDataAsync(
        { totalAmount: currentAmount },
        { totalAmount: previousAmount },
        'totalAmount',
        period,
        {
          collectionName: 'splits',
          valueExtractor: (docData) => parseFloat(docData.totalAmount || docData.amount || 0)
        }
      )
    ]).then(([splitsChart, amountChart]) => {
      setTotalSplitsChart(splitsChart);
      setTotalAmountChart(amountChart);
    }).catch(() => {
      // Fallback to synchronous generated data
      setTotalSplitsChart(transformToChartData(
        { totalSplits: currentValue },
        { totalSplits: previousValue },
        'totalSplits',
        period
      ));
      setTotalAmountChart(transformToChartData(
        { totalAmount: currentAmount },
        { totalAmount: previousAmount },
        'totalAmount',
        period
      ));
    });
  }, [splitData, previousSplitData, period]);

  // Get formatting functions from hook
  const { formatCurrency, formatNumber } = useFormatting();

  // Prepare table data for most active users
  const mostActiveUsersColumns = [
    { key: 'rank', label: 'Rank' },
    { key: 'name', label: 'User' },
    { 
      key: 'splitCount', 
      label: 'Splits',
      render: (value) => typeof value === 'number' ? formatNumber(value) : value
    },
    { 
      key: 'totalAmount', 
      label: 'Total Amount',
      render: (value) => typeof value === 'number' ? formatCurrency(value) : value
    }
  ];

  const mostActiveUsersData = useMemo(() => {
    if (!normalized.mostActiveUsers || normalized.mostActiveUsers.length === 0) return [];
    return normalized.mostActiveUsers.map((user, index) => ({
      id: user.userId || index,
      rank: index + 1,
      name: user.name || 'Unknown',
      splitCount: user.splitCount || 0,
      totalAmount: user.totalAmount || 0
    }));
  }, [normalized.mostActiveUsers, formatNumber, formatCurrency]);

  // Memoize split type chart data
  const splitTypeChartData = useMemo(() => {
    if (!normalized.fairSplits && !normalized.degenSplits) return null;
    return [
      { label: 'Fair Splits', value: normalized.fairSplits },
      { label: 'Degen Splits', value: normalized.degenSplits }
    ].filter(item => item.value > 0);
  }, [normalized.fairSplits, normalized.degenSplits]);

  // Memoize split status chart data
  const splitStatusChartData = useMemo(() => {
    if (!normalized.activeSplits && !normalized.completedSplits && !normalized.pendingSplits) return null;
    return [
      { label: 'Active', value: normalized.activeSplits },
      { label: 'Completed', value: normalized.completedSplits },
      { label: 'Pending', value: normalized.pendingSplits }
    ].filter(item => item.value > 0);
  }, [normalized.activeSplits, normalized.completedSplits, normalized.pendingSplits]);

  // Error state - must be after all hooks
  if (error) {
    return (
      <div className="split-metrics-enhanced error">
        <h3>Error loading split metrics</h3>
        <p>Please try refreshing the page</p>
      </div>
    );
  }

  return (
    <div className="split-metrics-enhanced">
      {/* Hero Section - Key KPIs */}
      <section className="split-hero">
        <div className="split-hero-header">
          <h1 className="split-title">
            <i className="bi bi-scissors icon-brand"></i>
            Split Analytics
          </h1>
          <p className="split-subtitle">{period} period</p>
        </div>
        
        <div className="split-kpi-grid">
          <KPICard
              title="Total Splits"
            value={normalized.totalSplits || 0}
              formatType="number"
            icon="bi-scissors"
            color="purple"
            subtitle={`${period} period`}
              loading={loading}
            />
          <KPICard
              title="Total Split Value"
            value={normalized.totalAmount || 0}
              formatType="currency"
            icon="bi-cash-stack"
            color="green"
            subtitle="Total amount"
              loading={loading}
            />
          <KPICard
            title="Average Split"
            value={normalized.averageSplitAmount || 0}
              formatType="currency"
            icon="bi-bar-chart"
            color="blue"
            subtitle="Per split"
              loading={loading}
          />
        </div>
      </section>

      {/* Split Status Overview */}
      <section className="split-section status-section">
        <div className="section-header">
          <h2 className="section-title">
            <i className="bi bi-diagram-3 icon-brand"></i>
            Split Status Overview
          </h2>
        </div>

        <div className="split-status-grid">
          <div className="status-card active-card">
            <div className="status-card-header">
              <i className="bi bi-check-circle status-icon"></i>
              <h3>Active Splits</h3>
            </div>
            <div className="status-card-content">
              <div className="status-stat">
                <span className="status-stat-label">Count</span>
                <span className="status-stat-value">{formatNumber(normalized.activeSplits || 0)}</span>
              </div>
              <div className="status-stat-mini">
                <span className="status-stat-mini-label">Percentage</span>
                <span className="status-stat-mini-value">{formatPercent(normalized.activePercentage || 0, 1)}</span>
              </div>
            </div>
          </div>

          <div className="status-card completed-card">
            <div className="status-card-header">
              <i className="bi bi-check-circle-fill status-icon"></i>
              <h3>Completed Splits</h3>
            </div>
            <div className="status-card-content">
              <div className="status-stat">
                <span className="status-stat-label">Count</span>
                <span className="status-stat-value">{formatNumber(normalized.completedSplits || 0)}</span>
              </div>
              <div className="status-stat-mini">
                <span className="status-stat-mini-label">Percentage</span>
                <span className="status-stat-mini-value">{formatPercent(normalized.completedPercentage || 0, 1)}</span>
              </div>
            </div>
          </div>

          <div className="status-card pending-card">
            <div className="status-card-header">
              <i className="bi bi-clock-history status-icon"></i>
              <h3>Pending Splits</h3>
            </div>
            <div className="status-card-content">
              <div className="status-stat">
                <span className="status-stat-label">Count</span>
                <span className="status-stat-value">{formatNumber(normalized.pendingSplits || 0)}</span>
              </div>
              <div className="status-stat-mini">
                <span className="status-stat-mini-label">Percentage</span>
                <span className="status-stat-mini-value">{formatPercent(normalized.pendingPercentage || 0, 1)}</span>
              </div>
          </div>
        </div>
        </div>
      </section>

      {/* Charts Section */}
      {totalSplitsChart && (
        <section className="split-section charts-section">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-graph-up icon-brand"></i>
              Split Trends
            </h2>
      </div>

          <div className="charts-grid">
            {totalSplitsChart && (
              <div className="chart-card">
                <h3 className="chart-title">Total Splits Over Time</h3>
                <AreaChart
                  data={totalSplitsChart.data}
                  comparisonData={totalSplitsChart.comparisonData}
                  label="Total Splits"
            formatType="number"
                  color="blue"
          />
        </div>
      )}

            {totalAmountChart && (
              <div className="chart-card">
                <h3 className="chart-title">Total Amount Over Time</h3>
                <AreaChart
                  data={totalAmountChart.data}
                  comparisonData={totalAmountChart.comparisonData}
                  label="Total Amount"
                  formatType="currency"
              color="green"
            />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Additional Metrics - Collapsible */}
      <CollapsibleSection title="Additional Split Metrics" defaultExpanded={false}>
        <div className="metrics-grid-wrapper">
          <div className="metrics-grid">
            {/* Split Types */}
            <div className="metric-group">
            <h3 className="group-title">Split Types</h3>
            <div className="metric-cards">
            <MetricCard
              title="Fair Splits"
              value={normalized.fairSplits}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-scale icon-brand"></i>}
              color="cyan"
              subtitle={formatPercent(normalized.fairPercentage, 1)}
            />
            <MetricCard
              title="Degen Splits"
              value={normalized.degenSplits}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-dice-5 icon-brand"></i>}
              color="purple"
              subtitle={formatPercent(normalized.degenPercentage, 1)}
            />
            <MetricCard
              title="Other Splits"
              value={normalized.otherSplits}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-bar-chart icon-brand"></i>}
              color="indigo"
              subtitle="remaining splits"
            />
          </div>
            </div>

            {/* Participants & Items */}
            <div className="metric-group">
            <h3 className="group-title">Participants & Items</h3>
            <div className="metric-cards">
            <MetricCard
              title="Total Participants"
              value={normalized.totalParticipants}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-people icon-brand"></i>}
              color="indigo"
              subtitle="across all splits"
            />
            <MetricCard
              title="Avg Participants"
              value={safeFormatFixed(normalized.averageParticipants, 1)}
              loading={loading}
              icon={<i className="bi bi-bar-chart icon-brand"></i>}
              color="cyan"
              subtitle="per split"
            />
            <MetricCard
              title="Total Items"
              value={normalized.totalItems}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-box-seam icon-brand"></i>}
              color="violet"
              subtitle="across all splits"
            />
            <MetricCard
              title="Avg Items"
              value={safeFormatFixed(normalized.averageItems, 1)}
              loading={loading}
              icon={<i className="bi bi-graph-up icon-brand"></i>}
              color="emerald"
              subtitle="per split"
            />
          </div>
            </div>

            {/* Financial Metrics */}
            <div className="metric-group">
            <h3 className="group-title">Financial Metrics</h3>
            <div className="metric-cards">
            <MetricCard
              title="Total Amount Owed"
              value={normalized.totalAmountOwed}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-cash-coin icon-brand"></i>}
              color="red"
              subtitle="across all participants"
            />
            <MetricCard
              title="Total Amount Paid"
              value={normalized.totalAmountPaid}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-check-circle icon-brand"></i>}
              color="green"
              subtitle="across all participants"
            />
            <MetricCard
              title="Payment Rate"
              value={formatPercent(normalized.paymentRate, 1)}
              loading={loading}
              icon={<i className="bi bi-graph-up-arrow icon-brand"></i>}
              color="blue"
              subtitle="paid vs owed"
            />
          </div>
            </div>

            {/* Split Details */}
            <div className="metric-group">
              <h3 className="group-title">Split Details</h3>
            <div className="metric-cards">
                <MetricCard
                  title="Split Status Ratio"
                  value={safeFormatFixed(normalized.splitStatusRatio, 2)}
                  loading={loading}
                  icon={<i className="bi bi-scale icon-brand"></i>}
                  color="teal"
                  subtitle="closed : active"
                />
                <MetricCard
                  title="Avg Invites/Split"
                  value={safeFormatFixed(normalized.averageInvitesPerSplit, 1)}
                  loading={loading}
                  icon={<i className="bi bi-person-plus icon-brand"></i>}
                  color="green"
                  subtitle="invites per split"
                />
            <MetricCard
              title="Split Wallets"
              value={normalized.splitWalletsCount}
              formatType="number"
              loading={loading}
              icon={<i className="bi bi-credit-card icon-brand"></i>}
              color="blue"
              subtitle="wallet count"
            />
            <MetricCard
              title="Wallet Amount"
              value={normalized.totalWalletAmount}
              formatType="currency"
              loading={loading}
              icon={<i className="bi bi-cash-coin icon-brand"></i>}
              color="green"
              subtitle="total wallet value"
            />
            </div>
            </div>
          </div>
        </div>

        {/* Charts in collapsible section */}
        <div className="charts-section-collapsible">
          {splitTypeChartData && (
            <div className="chart-card">
              <h3 className="chart-title">Split Type Distribution</h3>
              <PieChart
                data={splitTypeChartData}
                label="Split Type Distribution"
                formatType="number"
                showPercentages={true}
                innerRadius={0.5}
              />
            </div>
          )}

          {splitStatusChartData && (
            <div className="chart-card">
              <h3 className="chart-title">Split Status</h3>
              <BarChart
                data={splitStatusChartData}
                label="Split Status"
                formatType="number"
                orientation="vertical"
              />
            </div>
          )}
        </div>

      {/* Most Active Users Table */}
      {mostActiveUsersData.length > 0 && (
          <div className="table-section">
            <h3 className="table-title">Most Active Users</h3>
        <DataTable
          data={mostActiveUsersData}
          columns={mostActiveUsersColumns}
          loading={loading}
          searchable={true}
          sortable={true}
        />
          </div>
      )}
      </CollapsibleSection>
    </div>
  );
};

SplitMetrics.propTypes = {
  period: PropTypes.string.isRequired,
  compact: PropTypes.bool
};

SplitMetrics.defaultProps = {
  compact: false
};

export default React.memo(SplitMetrics);
